import { useEffect, useState } from 'react';

import APIHandler from 'config/APIHandler';

import { useSettings } from 'hooks/useSettings';

import type { DirectoryConfig, DirectoryConfigResponse } from './types';

const IS_DEV = import.meta.env.DEV;
const MOCK_DELAY_MS = 800;

const mockDelay = () => new Promise<void>((resolve) => setTimeout(resolve, MOCK_DELAY_MS));

const loadMockConfig = async (appId: string): Promise<DirectoryConfigResponse> => {
  await mockDelay();
  try {
    const module = (await import(`./mocks/directory-config-${appId}.json`)) as {
      default: DirectoryConfigResponse;
    };
    return module.default;
  } catch {
    const fallback = (await import('./mocks/directory-config-default.json')) as {
      default: DirectoryConfigResponse;
    };
    return fallback.default;
  }
};

type DirectoryConfigState =
  | { status: 'loading' }
  | { status: 'found'; config: DirectoryConfig }
  | { status: 'not_found' };

export const useDirectoryConfig = (useMock = false): DirectoryConfigState => {
  const { appId } = useSettings();
  const [state, setState] = useState<DirectoryConfigState>({ status: 'loading' });

  useEffect(() => {
    if (!appId) return;

    const fetchConfig = async () => {
      const shouldUseMock = useMock && IS_DEV;
      const response = (
        shouldUseMock ? await loadMockConfig(appId) : await APIHandler.getDirectoryConfig(appId)
      ) as DirectoryConfigResponse;

      if (response.status === 'success') {
        setState({ status: 'found', config: { map: response.map, listings: response.listings } });
      } else {
        setState({ status: 'not_found' });
      }
    };

    void fetchConfig();
  }, [appId, useMock]);

  return state;
};
